import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { EngagementManagerOption } from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/ValuationsTableRow.types';
import { AttioEngagementManagerData, AttioEngagementService } from 'dashboard409a/services/attio/AttioEngagementService';
import { SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';

interface UseEngagementManagerProps {
  valuationRow: SimplifiedAttioValuation;
  onEngagementManagerUpdate?: (
    recordId: string,
    engagementManagerOption: EngagementManagerOption
  ) => Promise<void>;
}

export const useEngagementManager = ({ valuationRow, onEngagementManagerUpdate }: UseEngagementManagerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [engagementManagers, setEngagementManagers] = useState<AttioEngagementManagerData[]>([]);
  const engagementManagerService = new AttioEngagementService();
  const { enqueueSnackbar } = useSnackbar();

  const updateMutation = useMutation({
    mutationFn: ({ 
      valuationId, 
      engagementManagerTargetRecordId,
    }: { 
      valuationId: string; 
      engagementManagerTargetRecordId: string;
      engagementManagerName: string;
      engagementManagerImageSrc: string;
    }) =>
      engagementManagerService.updateValuationEngagementManager({ valuationId, engagementManagerTargetRecordId }),
    onSuccess: async (_, variables) => {
      if (onEngagementManagerUpdate) {
        const engagementManagerOption: EngagementManagerOption = {
          targetRecordId: variables.engagementManagerTargetRecordId,
          name: variables.engagementManagerName,
          imageSrc: variables.engagementManagerImageSrc,
        };
        await onEngagementManagerUpdate(variables.valuationId, engagementManagerOption);
      }
      
      enqueueSnackbar('Engagement manager updated successfully!', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to update engagement manager. Please try again.', { variant: 'error' });
    },
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm('');
    setEngagementManagers([]);
  };

  const handleSelectEngagementManager = (engagementManagerTargetRecordId: string) => {
    // Find the selected engagement manager before clearing the array
    const selectedManager = engagementManagers.find(m => m.targetRecordId === engagementManagerTargetRecordId);
    
    if (!selectedManager) {
      enqueueSnackbar('Error: Could not find selected engagement manager', { variant: 'error' });
      return;
    }
    
    setAnchorEl(null);
    setSearchTerm('');
    setEngagementManagers([]);
    
    updateMutation.mutate({
      valuationId: valuationRow.record_id,
      engagementManagerTargetRecordId,
      engagementManagerName: selectedManager.name,
      engagementManagerImageSrc: selectedManager.imageSrc,
    });
  };

  const handleSearch = async () => {
    setSearching(true);
    if (searchTerm?.trim()) {
      const engagementManagersResult = await engagementManagerService.searchEngagementManagers(searchTerm);
      setEngagementManagers(engagementManagersResult);
      setSearching(false);
    } else {
      setSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchTerm.trim() && !searching) {
      handleSearch();
    }
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? 'engagement-manager-popover' : undefined;

  return {
    anchorEl,
    searchTerm,
    searching,
    engagementManagers,
    open,
    popoverId,
    handleClick,
    handleClose,
    handleSelectEngagementManager,
    handleSearch,
    handleKeyDown,
    setSearchTerm,
  };
};

